import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useDispatch } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

import { useMutation } from '@apollo/client/react'

import {
  Button,
  ChannelAvatar,
  CheckboxCard,
  CheckboxCardGroup,
  CriticalIcon,
  Dialog,
  EmptyState,
  Flex,
  Link,
  LoaderCircleIcon,
  Text,
  toast,
  WarningIcon,
} from '@buffer-mono/popcorn'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { isChannelType } from '~publish/helpers/channels'
import { graphql } from '~publish/graphql'
import { useCta } from '~publish/hooks/useCta'
import { actions as profileActions } from '~publish/legacy/profiles/reducer'
import { logError } from '~publish/legacy/utils/logError'
import { GetChannelGroups } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'
import {
  GetChannels,
  useChannelsForSettings,
} from '~publish/pages/Settings/Channels/hooks/useChannelsForSettings'
import type { ChannelMask } from '~publish/pages/Settings/Channels/hooks/useChannelsForSettings'

import style from './UnlockChannelDialog.module.css'

const LOCKED_CHANNELS_HELP_URL =
  'https://support.buffer.com/en-us/articles/unlocking-a-locked-channel-in-buffer-TWBz1u0FmT'

const UPGRADE_CTA = 'publish-channels-unlockChannelDialog-upgradePlan-1'

enum OptimumRequest {
  FULL_FETCHING = 'fullFetching',
  SINGLE_FETCHING = 'singleFetching',
}

const MAX_CHANNELS_FOR_SINGLE_FETCHING = 10
/**
 * Determine the optimum request type based on the number of channels to refresh
 * We use this to determine whether to fetch all profiles or a single profile
 * This is because fetching all profiles is more expensive than fetching a single profile
 * and we want to avoid fetching all profiles if we don't need to. However if a user was trying to
 * unlock a lot of channels, we want to fetch all profiles to avoid multiple requests.
 * @param channnelToRefresh - The number of channels to refresh
 * @returns The optimum request type
 */
const determineOptimumRequest = (channnelToRefresh: number): OptimumRequest => {
  if (channnelToRefresh <= MAX_CHANNELS_FOR_SINGLE_FETCHING) {
    return OptimumRequest.SINGLE_FETCHING
  }

  return OptimumRequest.FULL_FETCHING
}

export const LockChannelsMutation = graphql(/* GraphQL */ `
  mutation ChannelsLock(
    $organizationId: String
    $channelIdsToLock: [ID!]
    $channelIdsToUnlock: [ID!]
  ) {
    channelsLock(
      organizationId: $organizationId
      channelIdsToLock: $channelIdsToLock
      channelIdsToUnlock: $channelIdsToUnlock
    )
  }
`)

export const UnlockChannelDialog = ({
  children,
  title = 'Select Channels to Keep',
}: {
  children: JSX.Element
  title?: string
}): JSX.Element => {
  const dispatch = useDispatch()
  const organization = useCurrentOrganization()
  const organizationId = organization?.id
  const {
    channels,
    loading: loadingChannels,
    error: errorLoadingChannels,
  } = useChannelsForSettings({
    organizationId,
  })

  const [executeLockChannels, { loading: loadingResult }] =
    useMutation(LockChannelsMutation)

  const { clicked: upgradeCtaClicked } = useCta(UPGRADE_CTA, {
    upgradePathName: 'addChannel-upgrade',
  })

  const [isOpen, setIsOpen] = useState(false)
  const [updatedChannels, setUpdatedChannels] = useState<
    ChannelMask[] | undefined
  >(channels)
  // Track which channel IDs have changed
  const [changedChannelIds, setChangedChannelIds] = useState<Set<string>>(
    new Set(),
  )

  // Ensure state is fresh on each open
  useEffect(() => {
    if (isOpen) {
      setUpdatedChannels(channels)
      setChangedChannelIds(new Set())
    }
  }, [channels, isOpen])

  const handleChannelSelection = ({
    currentChannel,
    updatedChannels: nextChannels,
  }: {
    currentChannel?: ChannelMask
    updatedChannels?: ChannelMask[]
  }): void => {
    if (!currentChannel) return

    // Track this channel as changed
    const newChangedIds = new Set(changedChannelIds)

    // Find the original channel state
    const originalChannel = channels?.find((c) => c.id === currentChannel.id)
    const currentUpdatedChannel = nextChannels?.find(
      (c) => c.id === currentChannel.id,
    )

    // Determine if we're toggling back to original state or changing
    if (originalChannel && currentUpdatedChannel) {
      const willBeLocked = !currentUpdatedChannel.isLocked
      const isOriginalState = willBeLocked === originalChannel.isLocked

      if (isOriginalState) {
        // Remove from changed set if toggling back to original
        newChangedIds.delete(currentChannel.id)
      } else {
        // Add to changed set if different from original
        newChangedIds.add(currentChannel.id)
      }
    }

    setChangedChannelIds(newChangedIds)

    // Update the channel lock states
    const channelsWithUpdatedLockStates = nextChannels?.map((channel) => {
      if (channel.id === currentChannel?.id) {
        return {
          ...currentChannel,
          isLocked: !channel.isLocked,
        }
      } else {
        return channel
      }
    })
    setUpdatedChannels(channelsWithUpdatedLockStates)
  }

  const handleConfirmChannelLock = async (): Promise<void> => {
    try {
      const { data, error } = await executeLockChannels({
        variables: {
          organizationId,
          channelIdsToLock: updatedChannels
            ?.filter((c: ChannelMask) => c.isLocked)
            .map((c: ChannelMask) => c.id),
          channelIdsToUnlock: updatedChannels
            ?.filter((c: ChannelMask) => !c.isLocked)
            .map((c: ChannelMask) => c.id),
        },
        refetchQueries: [GetChannels, GetChannelGroups],
      })

      if (data?.channelsLock) {
        // Dispatch fetchSingleProfile for each changed channel
        if (changedChannelIds.size > 0) {
          const optimumRequest = determineOptimumRequest(changedChannelIds.size)
          if (optimumRequest === OptimumRequest.SINGLE_FETCHING) {
            changedChannelIds.forEach((channelId) => {
              dispatch(
                profileActions.fetchSingleProfile({
                  profileId: channelId,
                  message: '',
                }),
              )
            })
          } else if (optimumRequest === OptimumRequest.FULL_FETCHING) {
            // Trigger legacy rpc /profiles endpoint to fetch all profiles
            dispatch({ type: 'INIT_PROFILES' })
          }
        }

        toast.success(`You're all set! Your channels have been updated.`, {})
      }

      if (error) {
        toast.error('Failed to update channels.')
        const unexpectedError = new Error(
          error.message || 'Unexpected API response',
        )
        logError(unexpectedError, { metaData: { data, error } })
      }
    } catch (e) {
      toast.error('Failed to update channels.')
      const error = e instanceof Error ? e : new Error(String(e))
      logError(error, {
        metaData: { variables: { channels, updatedChannels } },
      })
    }
  }

  const numberOfUnlockedChannels =
    updatedChannels?.filter((channel) => !channel.isLocked).length || 0
  const channelLimit = organization?.limits.channels ?? 0
  const isOverChannelLimit = numberOfUnlockedChannels > channelLimit
  const noChannelsHaveBeenUpdated =
    JSON.stringify(updatedChannels) === JSON.stringify(channels)

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content>
        {loadingChannels ? (
          <EmptyState>
            <EmptyState.Icon>
              <LoaderCircleIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>Loading Channels</EmptyState.Heading>
            <Dialog.Close>
              <Button variant="secondary">Close</Button>
            </Dialog.Close>
          </EmptyState>
        ) : errorLoadingChannels ? (
          <EmptyState variant="critical">
            <EmptyState.Icon>
              <CriticalIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>Failed to load channels</EmptyState.Heading>
            <EmptyState.Description>
              Something went wrong. Please try again later.
            </EmptyState.Description>
            <Dialog.Close>
              <Button variant="secondary">Close</Button>
            </Dialog.Close>
          </EmptyState>
        ) : (
          <>
            <Dialog.Header>
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.Description>
                Your current plan supports up to {channelLimit} channels. Please
                select which {channelLimit} channels you&apos;d like to keep
                connected to Buffer. All others will be saved but locked.{' '}
                <Link external href={LOCKED_CHANNELS_HELP_URL}>
                  Learn more
                </Link>{' '}
                or{' '}
                <Link
                  as={RouterLink}
                  to="/settings/billing"
                  onClick={(): void => {
                    upgradeCtaClicked()
                    setIsOpen(false)
                  }}
                >
                  upgrade your plan
                </Link>{' '}
                for more channel slots.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <Flex align="center" justify="between">
                <Text>{channels?.length} Channels</Text>
                <Text>
                  <i>{numberOfUnlockedChannels} Selected</i>
                </Text>
              </Flex>
              <div className={style.checkboxGroup}>
                <CheckboxCardGroup>
                  {channels?.map((channel) => {
                    return (
                      <CheckboxCard
                        key={channel.id}
                        onCheckedChange={(): void =>
                          handleChannelSelection({
                            currentChannel: channel,
                            updatedChannels,
                          })
                        }
                        defaultChecked={!channel.isLocked}
                      >
                        <Flex gap="sm" align="center" translate="no">
                          {isChannelType(channel.service) && (
                            <ChannelAvatar
                              size="small"
                              alt={channel.name}
                              src={channel.avatar}
                              name={channel.name}
                              channel={channel.service}
                            />
                          )}
                          <Text>{channel.name || channel.displayName}</Text>
                        </Flex>
                      </CheckboxCard>
                    )
                  })}
                </CheckboxCardGroup>
              </div>
              {isOverChannelLimit ? (
                <Text
                  color="warning"
                  align="right"
                  icon={<WarningIcon color="warning" />}
                  className={style.warningText}
                >
                  A maximum number of {channelLimit} channels may be selected.
                </Text>
              ) : null}
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.Close>
                <Button variant="secondary">Cancel</Button>
              </Dialog.Close>
              <Dialog.Close>
                <Button
                  variant="primary"
                  onClick={(): void => {
                    void handleConfirmChannelLock()
                  }}
                  disabled={
                    noChannelsHaveBeenUpdated ||
                    isOverChannelLimit ||
                    loadingResult ||
                    numberOfUnlockedChannels === 0
                  }
                >
                  Save
                </Button>
              </Dialog.Close>
            </Dialog.Footer>
          </>
        )}
      </Dialog.Content>
    </Dialog>
  )
}
